import { useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = "https://www.lab336.ch";

let globalSeoCache = null;

export function useSeo() {
  useEffect(() => {
    if (globalSeoCache) {
      applySeo(globalSeoCache);
      return;
    }
    axios.get(`${API}/seo`).then(res => {
      globalSeoCache = res.data;
      applySeo(res.data);
    }).catch(() => {});
  }, []);

  return globalSeoCache;
}

export function usePageSeo({ title, description, canonical, robots = "index, follow", type = "website", jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta("description", description);
    setMeta("robots", robots);
    setCanonical(canonical || `${BASE_URL}${window.location.pathname}`);
    if (title) setMeta("og:title", title, "property");
    if (description) setMeta("og:description", description, "property");
    setMeta("og:type", type, "property");
    setMeta("og:url", canonical || `${BASE_URL}${window.location.pathname}`, "property");

    if (jsonLd) {
      applyJsonLd(jsonLd);
    }

    return () => {
      const pageLd = document.querySelector('script[data-page-ld]');
      if (pageLd) pageLd.remove();
    };
  }, [title, description, canonical, robots, type, jsonLd]);
}

function applySeo(seo) {
  if (!seo) return;
  if (seo.title) document.title = seo.title;
  setMeta("description", seo.description);
  setMeta("keywords", seo.keywords);
  setMeta("robots", "index, follow");
  if (seo.favicon_url) setFavicon(seo.favicon_url);

  setMeta("og:title", seo.title, "property");
  setMeta("og:description", seo.description, "property");
  setMeta("og:type", "website", "property");
  if (seo.og_image) setMeta("og:image", seo.og_image, "property");

  const canonicalUrl = seo.canonical_url || BASE_URL;
  setMeta("og:url", canonicalUrl, "property");
  // Only set canonical on homepage — subpages handle their own via usePageSeo
  if (window.location.pathname === "/" || window.location.pathname === "") {
    setCanonical(canonicalUrl);
  }

  setMeta("twitter:card", "summary_large_image");
  setMeta("twitter:title", seo.title);
  setMeta("twitter:description", seo.description);
  if (seo.og_image) setMeta("twitter:image", seo.og_image);

  applyStructuredData(seo);
}

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(url) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

function setFavicon(url) {
  let el = document.querySelector('link[rel="icon"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "icon");
    document.head.appendChild(el);
  }
  const type = url.endsWith(".svg") ? "image/svg+xml" : url.endsWith(".png") ? "image/png" : "image/x-icon";
  el.setAttribute("type", type);
  el.setAttribute("href", url);
}

function applyJsonLd(data) {
  const existing = document.querySelector('script[data-page-ld]');
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-page-ld", "true");
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

function applyStructuredData(seo) {
  const existing = document.querySelector('script[data-seo-ld]');
  if (existing) existing.remove();

  const graph = [];

  // Organization
  graph.push({
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`,
    "name": seo.company_name || "LAB336",
    "url": BASE_URL,
    "logo": `${BASE_URL}/favicon.svg`,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": seo.company_phone,
      "email": seo.company_email,
      "contactType": "customer service",
      "availableLanguage": ["de", "en"]
    }
  });

  // LocalBusiness
  graph.push({
    "@type": "LocalBusiness",
    "@id": `${BASE_URL}/#localbusiness`,
    "name": seo.company_name || "LAB336",
    "description": seo.description,
    "url": BASE_URL,
    "telephone": seo.company_phone,
    "email": seo.company_email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": seo.company_street,
      "postalCode": seo.company_zip,
      "addressLocality": seo.company_city,
      "addressCountry": seo.company_country || "CH"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Schweiz"
    },
    "priceRange": "$$"
  });

  // ProfessionalService
  graph.push({
    "@type": "ProfessionalService",
    "@id": `${BASE_URL}/#service`,
    "name": seo.company_name || "LAB336",
    "description": seo.description,
    "url": BASE_URL,
    "telephone": seo.company_phone,
    "email": seo.company_email,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": seo.company_street,
      "postalCode": seo.company_zip,
      "addressLocality": seo.company_city,
      "addressCountry": seo.company_country || "CH"
    },
    "areaServed": "CH",
    "serviceType": ["Prozessoptimierung", "Digitalisierung", "KI-Integration", "Automatisierung"]
  });

  // WebSite
  graph.push({
    "@type": "WebSite",
    "@id": `${BASE_URL}/#website`,
    "name": seo.company_name || "LAB336",
    "url": BASE_URL,
    "publisher": { "@id": `${BASE_URL}/#organization` }
  });

  // BreadcrumbList for homepage
  graph.push({
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": BASE_URL
      }
    ]
  });

  const ld = {
    "@context": "https://schema.org",
    "@graph": graph
  };

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-seo-ld", "true");
  script.textContent = JSON.stringify(ld);
  document.head.appendChild(script);
}

export { BASE_URL };
